import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ProductId, PRODUCT_CONFIGS } from '@/contexts/ProductContext';
import { Leaf, Sparkles, Check } from 'lucide-react';
import { getContrastTextColor } from '@/lib/utils/colorUtils';

interface ProductSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrolledProducts: string[];
}

// Product-specific icons and styling
const productConfig = {
  [ProductId.MINDCOTINE]: {
    Icon: Leaf,
    iconLabel: 'M',
    gradientClass: 'from-emerald-500 to-teal-600',
  },
  [ProductId.RELIEF]: {
    Icon: Sparkles,
    iconLabel: 'R',
    gradientClass: 'from-violet-500 to-purple-600',
  },
};

// Helper to get product translation key
const getProductTranslationKey = (productId: string) => {
  return productId === ProductId.MINDCOTINE ? 'mindcotine' : 'mindco-relief';
};

export const ProductSelectionModal = ({
  open,
  onOpenChange,
  enrolledProducts,
}: ProductSelectionModalProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<ProductId | null>(null);

  const handleContinue = () => {
    if (!selectedProduct) return;
    
    const config = PRODUCT_CONFIGS[selectedProduct];
    localStorage.setItem('selectedProductId', selectedProduct);
    onOpenChange(false);
    navigate(`${config.routing.pathPrefix}/dashboard`);
  };

  const availableProducts = Object.values(PRODUCT_CONFIGS).filter(
    (product) => enrolledProducts.includes(product.id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="text-center pb-2">
          <DialogTitle className="text-2xl font-bold text-center">
            {t('auth.selectProduct.title', 'Choose Your Program')}
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {t('auth.selectProduct.description', 'Select which program you\'d like to access today')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 mt-2">
          {availableProducts.map((product) => {
            const isSelected = selectedProduct === product.id;
            const { Icon, iconLabel, _gradientClass } = productConfig[product.id];
            const { primaryColor, secondaryColor } = product.branding;
            const primaryTextColor = getContrastTextColor(primaryColor);

            return (
              <button
                key={product.id}
                type="button"
                onClick={() => setSelectedProduct(product.id)}
                className={`
                  relative w-full text-left rounded-2xl p-5 transition-all duration-300
                  border-2 overflow-hidden group
                  ${isSelected 
                    ? 'shadow-xl scale-[1.02]' 
                    : 'border-border/50 hover:border-border hover:shadow-lg hover:scale-[1.01]'
                  }
                `}
                style={{
                  borderColor: isSelected ? primaryColor : undefined,
                  background: isSelected 
                    ? `linear-gradient(135deg, ${primaryColor}12, ${secondaryColor}20)` 
                    : undefined,
                }}
              >
                {/* Background glow effect when selected */}
                {isSelected && (
                  <div 
                    className="absolute inset-0 opacity-10 blur-2xl"
                    style={{ background: `radial-gradient(circle at 30% 50%, ${primaryColor}, transparent 60%)` }}
                  />
                )}

                <div className="relative flex items-center gap-5">
                  {/* Product Icon/Logo */}
                  <div 
                    className={`
                      flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center
                      shadow-lg transition-transform duration-300
                      ${isSelected ? 'scale-110' : 'group-hover:scale-105'}
                    `}
                    style={{ 
                      background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` 
                    }}
                  >
                    <div className="relative">
                      {/* Letter logo */}
                      <span className="font-bold text-2xl tracking-tight" style={{ color: primaryTextColor }}>
                        {iconLabel}
                      </span>
                      {/* Small icon badge */}
                      <div className="absolute -bottom-3 -right-4 w-5 h-5 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Icon className="w-3 h-3" style={{ color: primaryTextColor }} />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-xl font-bold tracking-tight">
                      {t(`products.${getProductTranslationKey(product.id)}.name`, product.name)}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                      {t(`products.${getProductTranslationKey(product.id)}.tagline`, product.branding.tagline)}
                    </p>
                  </div>

                  {/* Selection indicator */}
                  <div 
                    className={`
                      flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center
                      transition-all duration-300 border-2
                      ${isSelected 
                        ? 'scale-100 opacity-100' 
                        : 'scale-75 opacity-0 group-hover:scale-90 group-hover:opacity-50'
                      }
                    `}
                    style={{ 
                      backgroundColor: isSelected ? primaryColor : 'transparent',
                      borderColor: primaryColor,
                    }}
                  >
                    <Check className="w-4 h-4" style={{ color: isSelected ? primaryTextColor : 'transparent' }} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <Button
          onClick={handleContinue}
          disabled={!selectedProduct}
          className="w-full mt-4 h-12 text-base font-semibold rounded-xl"
          style={selectedProduct ? {
            background: `linear-gradient(135deg, ${PRODUCT_CONFIGS[selectedProduct].branding.primaryColor}, ${PRODUCT_CONFIGS[selectedProduct].branding.secondaryColor})`,
          } : undefined}
        >
          {t('auth.continue', 'Continue')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};